import { useMemo } from "react";
import { flatten, isEmpty, isNil } from "./functions";
import Logger from "@/src/exceptions/Logger";
import ValidationRule, { RuleMessage } from "./ValidationRule";

interface IRule {
    [name:string]: Array<string | Function>;
}

type ObjectType = {
    [name: string]: string[] | string;
}

type FormFieldType = {
    [name:string]: any;
}

interface ValidatorMakeProps {
    fields: FormFieldType;
    rules: IRule;
    message?: Object;
}


class FormValidator {
    private formInputs:any;
    private rules:any;
    private _errors: ObjectType = {};

    public make({fields, rules}: ValidatorMakeProps) {
        this.formInputs = flatten (fields);
        this.rules = rules;

        this.validate();

        return this;
    }

    private validate() {
        this.clear();

        let errors:any = this._errors;

        Logger.info("Form Field (Flatten)", this.formInputs);

        for (const fieldName in this.rules) {
            const rules = this.rules[fieldName]
            const fieldValue:any = this.formInputs[fieldName];

            errors[fieldName] = [];

            for (let i=0; i<rules.length; i++) {
                const ruleMethod:any = rules[i];
                if (typeof ruleMethod === "string") {
                    const passed:boolean = this.callRuleMethod(ruleMethod, fieldValue);

                    // Logger.log(`Validation ${ruleMethod} rule passed: `, passed)
    
                    if (!passed) {
                        errors[fieldName].push(this.getRuleMessage(ruleMethod, fieldName));
                    }
                } else {
                    // Impelement function rule.
                }
            }
        }

        this._errors = errors;
    }

    protected callRuleMethod(method:string, fieldValue: any) {
        const instance: any = ValidationRule;
        if (typeof instance[method] == "function") {
            return instance[method](fieldValue);
        }
        throw `${method} rule is not defined in ValidateRule.${method}().`;
    }

    protected getRuleMessage(method:string, fieldName:string)
    {
        const message:string = RuleMessage[method];

        if (message) {
            return message.replace(":attribute", fieldName)
        }
        return "";
    }

    public fails (): boolean {
        if (!isEmpty(this._errors)) {
            const errorValues:any[] = Object.values(this._errors);
            const errorBools: boolean[] = errorValues.map((err:any) => !isEmpty(err));
            Logger.info("Form Validation Errors (fails): ", errorBools);
            Logger.info("Form Validation Errors (fails): ", errorBools.includes(true));
            return errorBools.includes(true);
        }

        return false;
    }

    public errors() {
        return this._errors;
    }

    public hasErrors(fieldName:string): boolean
    {
        const errorKeys: string[] = Object.keys(this._errors);
        
        Logger.info("Error Keys", errorKeys);
        Logger.info("Error Keys", this._errors);

        if (errorKeys.includes(fieldName)) {
            return !isEmpty (this._errors[fieldName]);
        }
        return false;
    }

    public getError(fieldName:string)
    {
        const errorKeys: string[] = Object.keys(this._errors);

        if (!isNil(fieldName) && errorKeys.includes(fieldName)) {
            if (Array.isArray(this._errors[fieldName])) {
                return !isEmpty(this._errors[fieldName])? this._errors[fieldName][0] : "";
            } else {
                return this._errors[fieldName];
            }
        }
        return "";
    }

    public setError(errorObj:ObjectType) 
    {
        this._errors = errorObj;
    }

    public clear(fieldName?:string)
    {
        if (!isEmpty(this._errors)) {
            if (!isNil(fieldName) && Object.keys(this._errors).includes(fieldName!)) {
                this._errors[fieldName!] = [];
            } else {
                this._errors = {};
            }
        }
    }
}

export const useValidation = () => {
    const validation = useMemo(() => new FormValidator(), []);
    return validation;
}

export default FormValidator;